import { connect } from 'react-redux';
import lodash from 'lodash';
import Popover from 'components/common/Popover/Popover';
import styles from './AddingDocumentIndicator.scss';
import { SpinnerMini } from 'components/common/LoadingStatus/LoadingStatus';
import React, { useState } from 'react';
import { dismissFailedDocumentsAccordionAction } from 'common/actions/fileUploadActions';

const retrieveNewDocuments = ({ documentConversionStatuses }) => {
    const allDocuments = lodash.values(documentConversionStatuses);
    return lodash.filter(allDocuments, ['status', 'new']);
};

const FailedDocumentsIndicator = ({ documentsUploadFailed }) => {

    const itemName = documentsUploadFailed.length === 1 ? 'document' : 'documents';
    const title = `${documentsUploadFailed.length} ${itemName} failed...`;

    return (
        <div className={styles.addingDocumentsContainer}>
            <div className={styles.failedDocumentsIcon}>warning</div>
            <div className={styles.addingDocumentsTitle}>{title}</div>
        </div>
    );
};

const FailedDocsIcon = ({ documentsUploadFailed }) => {

    if (lodash.isEmpty(documentsUploadFailed)) {
        return null;
    }

    return <div className={styles.failedDocumentsIcon}>warning</div>;
};

const AddingDocumentIndicator = ({ documentConversionStatuses, documentsUploadFailed }) => {
    
    const newDocuments = retrieveNewDocuments({ documentConversionStatuses });

    if (lodash.isEmpty(newDocuments) ) {

        if (lodash.isEmpty(documentsUploadFailed)) {
            return null;
        }

        return <FailedDocumentsIndicator documentsUploadFailed={documentsUploadFailed}/>;
    }

    const itemName = newDocuments.length === 1 ? 'document' : 'documents';
    const title = `Adding ${newDocuments.length} ${itemName}...`;

    return (
        <div className={styles.addingDocumentsContainer}>
            <FailedDocsIcon documentsUploadFailed={documentsUploadFailed} />
            <div className={styles.addingDocumentsTitle}>{title}</div>
            <span><SpinnerMini extraSmall={true} /></span>
        </div>
    );
};

const FailedDocuments = ({ documentsUploadFailed }) => {
    if (lodash.isEmpty(documentsUploadFailed)) {
        return null;
    }

    return (
        <ul>
            {
                documentsUploadFailed.map((doc, idx) => {
                    return (
                        <li className={styles.menuLink} key={idx}>
                            {doc.name}
                        </li>
                    );
                })
            }
        </ul>
    );
};

const ConvertingDocuments = ({ documentConversionStatuses }) => {
    const newDocuments = retrieveNewDocuments({ documentConversionStatuses });

    if (lodash.isEmpty(newDocuments)) {
        return null;
    }

    return (
        <ul>
            {
                newDocuments.map((doc, idx) => {
                    return (
                        <li className={styles.menuLink} key={idx}>
                            <span className={styles.menuLinkText}>{doc.name}</span>
                            <span><SpinnerMini extraSmall={true} /></span>                            
                        </li>
                    );
                })
            }
        </ul>
    );
};

export const AddingDocumentPopover = ({
    documentConversionStatuses,
    documentsUploadFailed,
    dismissFailedDocumentsAccordionAction
}) => {
    const [isFailedList, setIsFailedList] = useState(false);

    const handleFailDocsClick =  (event) => {
        // Prevent the popover from closing
        event.stopPropagation();
        setIsFailedList(!isFailedList);
    };

    const handleClearFailedDocs = (event) => {
        // Prevent the popover from closing
        event.stopPropagation();
        dismissFailedDocumentsAccordionAction();
    };

    const documentIndicator =
        <AddingDocumentIndicator
            documentConversionStatuses={documentConversionStatuses}
            documentsUploadFailed={documentsUploadFailed}
        />;

    const failedDocumentsLabel = documentsUploadFailed && documentsUploadFailed.length === 1 ? 'Document' : 'Documents';

    const newDocuments = retrieveNewDocuments({ documentConversionStatuses });
    const failedDocsWithUploadingDocs = !lodash.isEmpty(documentsUploadFailed) && !lodash.isEmpty(newDocuments);
    const failedDocsWithoutUploadingDocs = !lodash.isEmpty(documentsUploadFailed) && lodash.isEmpty(newDocuments);

    return (
        <Popover openDirection={'right'} target={documentIndicator} data-cy="AddingDocumentIndicator">
            {documentIndicator}

            {   // Shows the Failed Docs link when there are uploading documents.
                failedDocsWithUploadingDocs ?
                    <div className={styles.failDocsLink} onClick={handleFailDocsClick}>
                        {
                            !isFailedList 
                                ?
                                <div className={styles.secondaryMenu}>
                                    <div className={styles.failedDocumentsIcon}>warning</div>
                                    {documentsUploadFailed.length} {failedDocumentsLabel} Failed
                                    <span className={styles.secondaryMenuIcon}>keyboard_arrow_right</span>
                                </div>
                                :
                                <div className={styles.secondaryMenu}>
                                    <span className={styles.secondaryMenuIcon}>keyboard_arrow_left</span>
                                    Back
                                </div>
                        }
                    </div>
                    :
                    null
            }

            {   // Shows a list of all the failed docs with a clear all button when there's no uploading documents.
                failedDocsWithoutUploadingDocs ?
                    <React.Fragment>
                        <div class={styles.clearAllBtnWrapper}>
                            <div role="button" class={styles.clearAllBtn} onClick={handleClearFailedDocs}>Clear all</div>
                        </div>
                        <FailedDocuments documentsUploadFailed={documentsUploadFailed}/>
                    </React.Fragment>
                    : null
            }

            {   // Toggle list to show when there's document uploading.
                !lodash.isEmpty(newDocuments) && isFailedList ? 
                    <FailedDocuments documentsUploadFailed={documentsUploadFailed}/>
                    :
                    <ConvertingDocuments documentConversionStatuses={documentConversionStatuses}/>
            }
        </Popover>
    );
};

export default connect((state, props) => {
    const documentConversionStatuses = lodash.get(state, 'documentConversionStatuses');
    const documentsUploadFailed = lodash.get(state, 'documentsUploadFailed');
    
    return {
        documentConversionStatuses,
        documentsUploadFailed,
    };
}, {
    dismissFailedDocumentsAccordionAction
})(AddingDocumentPopover);
